export const NAVBAR_CLOCK_PREFERENCES_STORAGE_KEY = 'bwc-navbar-clock-preferences-v1'
export const NAVBAR_CLOCK_PREFERENCES_CHANGE_EVENT = 'bwc-navbar-clock-preferences-change'

export const DEFAULT_NAVBAR_CLOCK_PREFERENCES = Object.freeze({
  size: 'medium',
  font: 'digital',
  colors: Object.freeze({
    light: null,
    dark: null,
  }),
})

export const NAVBAR_CLOCK_SIZE_OPTIONS = Object.freeze([
  { value: 'small', label: 'Small', className: 'w-20 text-sm' },
  { value: 'medium', label: 'Medium', className: 'w-24 text-base' },
  { value: 'large', label: 'Large', className: 'w-28 text-lg' },
  { value: 'extra-large', label: 'Extra Large', className: 'w-32 text-xl' },
])

export const NAVBAR_CLOCK_FONT_OPTIONS = Object.freeze([
  { value: 'digital', label: 'Digital', className: 'font-digital' },
  { value: 'terminal', label: 'Terminal', className: 'font-terminal' },
  { value: 'classic', label: 'Classic Mono', className: 'font-classic' },
])

const VALID_SIZES = new Set(NAVBAR_CLOCK_SIZE_OPTIONS.map(option => option.value))
const VALID_FONTS = new Set(NAVBAR_CLOCK_FONT_OPTIONS.map(option => option.value))

export function normalizeNavbarClockColor(value) {
  if (typeof value !== 'string') return null
  const normalized = value.trim().toUpperCase()
  return /^#[0-9A-F]{6}$/.test(normalized) ? normalized : null
}

export function sanitizeNavbarClockPreferences(value) {
  const candidate = value && typeof value === 'object' ? value : {}
  const legacyColor = normalizeNavbarClockColor(candidate.color)
  const modeColors = candidate.colors && typeof candidate.colors === 'object'
    ? candidate.colors
    : null

  return {
    size: VALID_SIZES.has(candidate.size)
      ? candidate.size
      : DEFAULT_NAVBAR_CLOCK_PREFERENCES.size,
    font: VALID_FONTS.has(candidate.font)
      ? candidate.font
      : DEFAULT_NAVBAR_CLOCK_PREFERENCES.font,
    colors: {
      light: modeColors
        ? normalizeNavbarClockColor(modeColors.light)
        : legacyColor,
      dark: modeColors
        ? normalizeNavbarClockColor(modeColors.dark)
        : legacyColor,
    },
  }
}

export function readNavbarClockPreferences() {
  if (typeof window === 'undefined') {
    return {
      ...DEFAULT_NAVBAR_CLOCK_PREFERENCES,
      colors: { ...DEFAULT_NAVBAR_CLOCK_PREFERENCES.colors },
    }
  }

  try {
    const stored = window.localStorage.getItem(NAVBAR_CLOCK_PREFERENCES_STORAGE_KEY)
    return stored
      ? sanitizeNavbarClockPreferences(JSON.parse(stored))
      : {
          ...DEFAULT_NAVBAR_CLOCK_PREFERENCES,
          colors: { ...DEFAULT_NAVBAR_CLOCK_PREFERENCES.colors },
        }
  } catch {
    return {
      ...DEFAULT_NAVBAR_CLOCK_PREFERENCES,
      colors: { ...DEFAULT_NAVBAR_CLOCK_PREFERENCES.colors },
    }
  }
}

function notifyNavbarClockPreferences(preferences) {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(NAVBAR_CLOCK_PREFERENCES_CHANGE_EVENT, {
    detail: preferences,
  }))
}

export function writeNavbarClockPreferences(value) {
  const preferences = sanitizeNavbarClockPreferences(value)

  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(
        NAVBAR_CLOCK_PREFERENCES_STORAGE_KEY,
        JSON.stringify(preferences)
      )
    } catch {
      // Keep the current tab usable if browser storage is unavailable.
    }
    notifyNavbarClockPreferences(preferences)
  }

  return preferences
}

export function resetNavbarClockPreferences() {
  const preferences = {
    ...DEFAULT_NAVBAR_CLOCK_PREFERENCES,
    colors: { ...DEFAULT_NAVBAR_CLOCK_PREFERENCES.colors },
  }

  if (typeof window !== 'undefined') {
    try {
      window.localStorage.removeItem(NAVBAR_CLOCK_PREFERENCES_STORAGE_KEY)
    } catch {
      // Keep the current tab usable if browser storage is unavailable.
    }
    notifyNavbarClockPreferences(preferences)
  }

  return preferences
}

export function subscribeNavbarClockPreferences(listener) {
  if (typeof window === 'undefined') return () => {}

  const handlePreferenceChange = event => {
    listener(sanitizeNavbarClockPreferences(event.detail))
  }
  const handleStorageChange = event => {
    if (event.key !== null && event.key !== NAVBAR_CLOCK_PREFERENCES_STORAGE_KEY) return
    listener(readNavbarClockPreferences())
  }

  window.addEventListener(NAVBAR_CLOCK_PREFERENCES_CHANGE_EVENT, handlePreferenceChange)
  window.addEventListener('storage', handleStorageChange)

  return () => {
    window.removeEventListener(NAVBAR_CLOCK_PREFERENCES_CHANGE_EVENT, handlePreferenceChange)
    window.removeEventListener('storage', handleStorageChange)
  }
}
